// src/utils/letterPDF.ts
// ──────────────────────────────────────────────────────────────
// Client-side PDF generator for prior auth and appeal letters.
// Uses jsPDF to produce a professional, fax-ready PDF directly
// in the browser. No PHI ever leaves the device.
//
// Usage:
//   import { generateLetterPDF } from "../utils/letterPDF";
//   const blob = generateLetterPDF(letterText, { filename: "prior-auth.pdf" });
// ──────────────────────────────────────────────────────────────

import { jsPDF } from "jspdf";

// ── Types ────────────────────────────────────────────────────

interface PDFOptions {
  filename?: string;
  /** Auto-download the file (default: true) */
  download?: boolean;
  /** Return the blob for further processing */
  returnBlob?: boolean;
}

interface ParsedLine {
  text: string;
  type:
    | "header" // Practice name / address block
    | "date" // Date line
    | "addressee" // Medical Director / Department / Company
    | "field" // RE:, Patient:, DOB:, etc.
    | "section" // ALL CAPS section header
    | "salutation" // Dear ...,
    | "body" // Normal paragraph text
    | "citation" // Numbered item (1. ...)
    | "closing" // Sincerely,
    | "signature" // Physician name, NPI, practice
    | "empty"; // Blank line
}

// ── Layout constants ─────────────────────────────────────────

const PAGE = {
  WIDTH: 215.9, // 8.5 inches in mm
  HEIGHT: 279.4, // 11 inches in mm
  MARGIN_TOP: 25.4, // 1 inch
  MARGIN_BOTTOM: 25.4,
  MARGIN_LEFT: 25.4,
  MARGIN_RIGHT: 25.4,
};

const FONT = {
  BODY_SIZE: 10.5,
  HEADER_SIZE: 10.5,
  SECTION_SIZE: 10.5,
  FIELD_SIZE: 10.5,
  CITATION_SIZE: 10,
  SIGNATURE_SIZE: 10.5,
  LINE_HEIGHT: 1.4, // Multiplier
};

const CONTENT_WIDTH = PAGE.WIDTH - PAGE.MARGIN_LEFT - PAGE.MARGIN_RIGHT;

// ── Text classifier ──────────────────────────────────────────

function classifyLine(
  line: string,
  lineIndex: number,
  allLines: string[]
): ParsedLine["type"] {
  const trimmed = line.trim();

  if (!trimmed) return "empty";

  // Section headers: ALL CAPS, 3+ chars, typically section titles
  if (/^[A-Z][A-Z\s&]{2,}$/.test(trimmed) && trimmed.length >= 3) {
    return "section";
  }

  // Field lines: "Key: Value" format
  if (
    /^(RE|Patient|Date of Birth|Member ID|Plan|Service Requested|Date of Service|Diagnosis|Reason for Denial|Date of Denial|NPI):/.test(
      trimmed
    )
  ) {
    return "field";
  }

  // Phone/Fax line
  if (/^Phone:/.test(trimmed)) {
    return "field";
  }

  // Date line (e.g., "February 23, 2026")
  if (
    /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/.test(
      trimmed
    )
  ) {
    return "date";
  }

  // Salutation
  if (/^Dear\s/.test(trimmed)) {
    return "salutation";
  }

  // Closing
  if (/^(Sincerely|Respectfully|Best regards|Regards),?\s*$/i.test(trimmed)) {
    return "closing";
  }

  // Numbered citation (1. ..., 2. ...)
  if (/^\d+\.\s/.test(trimmed)) {
    return "citation";
  }

  // Addressee block: lines 4-8 after the header (Medical Director, Department, Company)
  // Detect by position — after date, before RE:
  const prevNonEmpty = allLines
    .slice(0, lineIndex)
    .reverse()
    .find((l) => l.trim());
  if (prevNonEmpty) {
    const prevType = classifyLineSimple(prevNonEmpty.trim());
    if (prevType === "date" || prevType === "addressee") {
      // Lines after date and before a field line are addressee
      if (!/^(RE|Patient|Dear)/.test(trimmed)) {
        return "addressee";
      }
    }
  }

  // First few non-empty lines are header (practice info)
  let nonEmptyCount = 0;
  for (let i = 0; i <= lineIndex; i++) {
    if (allLines[i].trim()) nonEmptyCount++;
  }
  if (nonEmptyCount <= 3 && lineIndex < 5) {
    return "header";
  }

  // Lines after "Sincerely" are signature
  for (let i = lineIndex - 1; i >= 0; i--) {
    const prevTrimmed = allLines[i].trim();
    if (
      /^(Sincerely|Respectfully|Best regards|Regards),?\s*$/i.test(prevTrimmed)
    ) {
      return "signature";
    }
    if (prevTrimmed) break; // Stop at first non-empty non-closing line
  }

  return "body";
}

// Simple version without context for recursive calls
function classifyLineSimple(trimmed: string): string {
  if (!trimmed) return "empty";
  if (
    /^(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}$/.test(
      trimmed
    )
  )
    return "date";
  if (
    /^(Medical Director|Appeals Department|Prior Authorization Department)/.test(
      trimmed
    )
  )
    return "addressee";
  return "body";
}

// ── PDF Generator ────────────────────────────────────────────

export function generateLetterPDF(
  letterText: string,
  options: PDFOptions = {}
): Blob | void {
  const {
    filename = "prior-authorization-letter.pdf",
    download = true,
    returnBlob = false,
  } = options;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "letter",
  });

  let y = PAGE.MARGIN_TOP;
  const lines = letterText.split("\n");

  // Track state for spacing
  let lastType: ParsedLine["type"] = "empty";
  let afterClosing = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const type = classifyLine(line, i, lines);

    // Check if we need a new page
    const neededHeight = getLineHeight(type);
    if (y + neededHeight > PAGE.HEIGHT - PAGE.MARGIN_BOTTOM) {
      doc.addPage();
      y = PAGE.MARGIN_TOP;
    }

    // ── Spacing ──────────────────────────────────────────

    if (type === "empty") {
      // Empty lines add spacing based on context
      if (
        lastType === "header" ||
        lastType === "date" ||
        lastType === "addressee"
      ) {
        y += 3;
      } else if (lastType === "section") {
        y += 1;
      } else if (lastType === "closing") {
        y += 8; // Signature space
        afterClosing = true;
      } else {
        y += 3.5;
      }
      lastType = type;
      continue;
    }

    // ── Render by type ───────────────────────────────────

    switch (type) {
      case "header": {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(FONT.HEADER_SIZE);
        doc.setTextColor(15, 23, 42); // slate-900
        const wrapped = doc.splitTextToSize(trimmed, CONTENT_WIDTH);
        doc.text(wrapped, PAGE.MARGIN_LEFT, y);
        y += wrapped.length * FONT.HEADER_SIZE * 0.4;
        break;
      }

      case "date": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.BODY_SIZE);
        doc.setTextColor(51, 65, 85); // slate-700
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);
        y += FONT.BODY_SIZE * 0.4;
        break;
      }

      case "addressee": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.BODY_SIZE);
        doc.setTextColor(51, 65, 85);
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);
        y += FONT.BODY_SIZE * 0.4;
        break;
      }

      case "field": {
        // Bold the label, normal the value
        const colonIdx = trimmed.indexOf(":");
        const label = trimmed.slice(0, colonIdx + 1);
        const value = trimmed.slice(colonIdx + 1).trim();

        doc.setFont("helvetica", "bold");
        doc.setFontSize(FONT.FIELD_SIZE);
        doc.setTextColor(15, 23, 42);
        doc.text(label, PAGE.MARGIN_LEFT, y);

        const labelWidth = doc.getTextWidth(label);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);

        // Wrap the value if it's long
        const remainingWidth = CONTENT_WIDTH - labelWidth - 2;
        if (doc.getTextWidth(value) > remainingWidth && remainingWidth > 30) {
          const wrapped = doc.splitTextToSize(value, remainingWidth);
          doc.text(wrapped[0], PAGE.MARGIN_LEFT + labelWidth + 2, y);
          for (let w = 1; w < wrapped.length; w++) {
            y += FONT.FIELD_SIZE * 0.4;
            doc.text(wrapped[w], PAGE.MARGIN_LEFT + labelWidth + 2, y);
          }
        } else {
          doc.text(` ${value}`, PAGE.MARGIN_LEFT + labelWidth, y);
        }
        y += FONT.FIELD_SIZE * 0.4;
        break;
      }

      case "section": {
        // Add extra space before section
        if (lastType !== "empty") y += 4;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(FONT.SECTION_SIZE);
        doc.setTextColor(15, 23, 42);
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);

        // Underline
        const textWidth = doc.getTextWidth(trimmed);
        y += 1;
        doc.setDrawColor(203, 213, 225); // slate-300
        doc.setLineWidth(0.3);
        doc.line(PAGE.MARGIN_LEFT, y, PAGE.MARGIN_LEFT + textWidth, y);
        y += 3;
        break;
      }

      case "salutation": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.BODY_SIZE);
        doc.setTextColor(15, 23, 42);
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);
        y += FONT.BODY_SIZE * 0.4;
        break;
      }

      case "body": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.BODY_SIZE);
        doc.setTextColor(51, 65, 85);

        const wrapped = doc.splitTextToSize(trimmed, CONTENT_WIDTH);
        for (const wLine of wrapped) {
          // Check for page break mid-paragraph
          if (y + FONT.BODY_SIZE * 0.4 > PAGE.HEIGHT - PAGE.MARGIN_BOTTOM) {
            doc.addPage();
            y = PAGE.MARGIN_TOP;
          }
          doc.text(wLine, PAGE.MARGIN_LEFT, y);
          y += FONT.BODY_SIZE * FONT.LINE_HEIGHT * 0.38;
        }
        break;
      }

      case "citation": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.CITATION_SIZE);
        doc.setTextColor(51, 65, 85);

        // Indent citations slightly
        const indent = 5;
        const wrapped = doc.splitTextToSize(trimmed, CONTENT_WIDTH - indent);
        for (let w = 0; w < wrapped.length; w++) {
          if (y + FONT.CITATION_SIZE * 0.4 > PAGE.HEIGHT - PAGE.MARGIN_BOTTOM) {
            doc.addPage();
            y = PAGE.MARGIN_TOP;
          }
          doc.text(wrapped[w], PAGE.MARGIN_LEFT + (w === 0 ? 0 : indent), y);
          y += FONT.CITATION_SIZE * FONT.LINE_HEIGHT * 0.38;
        }
        break;
      }

      case "closing": {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(FONT.BODY_SIZE);
        doc.setTextColor(15, 23, 42);
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);
        y += FONT.BODY_SIZE * 0.4;
        afterClosing = true;
        break;
      }

      case "signature": {
        doc.setFont("helvetica", afterClosing ? "bold" : "normal");
        doc.setFontSize(FONT.SIGNATURE_SIZE);
        doc.setTextColor(15, 23, 42);
        doc.text(trimmed, PAGE.MARGIN_LEFT, y);
        y += FONT.SIGNATURE_SIZE * 0.4;
        afterClosing = false; // Only first sig line is bold
        break;
      }
    }

    lastType = type;
  }

  // ── Output ─────────────────────────────────────────────

  const blob = doc.output("blob");

  if (download) {
    doc.save(filename);
  }

  if (returnBlob) {
    return blob;
  }
}

// ── Helpers ──────────────────────────────────────────────────

function getLineHeight(type: ParsedLine["type"]): number {
  switch (type) {
    case "section":
      return 8;
    case "body":
      return 5;
    default:
      return 4.5;
  }
}

/**
 * Generate a safe filename from case details.
 */
export function getLetterFilename(
  patientName?: string,
  requestType?: string,
  isAppeal?: boolean
): string {
  const prefix = isAppeal ? "Appeal" : "Prior-Auth";
  const name = patientName
    ? patientName
        .replace(/[^a-zA-Z0-9]/g, "-")
        .replace(/-+/g, "-")
        .slice(0, 30)
    : "Letter";
  const date = new Date().toISOString().slice(0, 10);
  return `${prefix}_${name}_${date}.pdf`;
}
